import React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer } from '../hooks/useScrollAnimation';
import { MapPin, GraduationCap, Briefcase, Award } from 'lucide-react';
export function AboutSection() {
  const highlights = [{
    icon: <Briefcase className="w-4 h-4" data-id="element-16" />,
    label: '20+ Years',
    sub: 'Enterprise Technology'
  }, {
    icon: <Award className="w-4 h-4" data-id="element-17" />,
    label: 'Fortune 100',
    sub: 'CTO Experience'
  }, {
    icon: <GraduationCap className="w-4 h-4" data-id="element-18" />,
    label: 'Wharton',
    sub: 'CTO Executive Program'
  }, {
    icon: <MapPin className="w-4 h-4" data-id="element-19" />,
    label: 'Charlotte, NC',
    sub: 'Open to Relocation'
  }];
  return <section id="about" className="py-20 md:py-32 bg-dark-bg border-t border-dark-border/30" data-id="element-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-21">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} data-id="element-22">
          <motion.div variants={fadeInUp} className="mb-16" data-id="element-23">
            <span className="font-sans text-gold/80 tracking-widest uppercase text-xs font-semibold mb-3 block" data-id="element-24">
              About
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white" data-id="element-25">
              The Executive Behind the Numbers
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start" data-id="element-26">
            {/* Left Column — Avatar + Highlights */}
            <motion.div variants={fadeInUp} className="lg:col-span-2 flex flex-col gap-8" data-id="element-27">
              {/* Headshot */}
              <div className="flex items-center gap-5" data-id="element-28">
                <div className="w-24 h-24 rounded-xl border-2 border-gold/40 overflow-hidden flex-shrink-0" data-id="element-29">
                  <img src="https://cdn.magicpatterns.com/uploads/vsJvP3zAKHLjaDrNN2Q55Q/michael_headshot.jpg" alt="Michael Parks" className="w-full h-full object-cover object-top" data-id="element-30" />
                </div>
                <div data-id="element-31">
                  <div className="text-white font-bold text-xl" data-id="element-32">
                    Michael Parks
                  </div>
                  <div className="text-gold/70 text-sm mt-1" data-id="element-33">
                    Chief Technology Officer
                  </div>
                  <div className="flex items-center gap-2 mt-2" data-id="element-34">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" data-id="element-35"></span>
                    <span className="text-green-400 text-xs font-medium" data-id="element-36">
                      Open to Opportunities
                    </span>
                  </div>
                </div>
              </div>

              {/* Highlight Stats */}
              <div className="grid grid-cols-2 gap-3" data-id="element-37">
                {highlights.map((h, i) => <div key={i} className="bg-dark-card border border-dark-border rounded-lg p-4 flex flex-col gap-2" data-id="element-38">
                    <div className="text-gold/70" data-id="element-39">{h.icon}</div>
                    <div className="text-white font-bold text-sm" data-id="element-40">
                      {h.label}
                    </div>
                    <div className="text-slate-500 text-xs" data-id="element-41">{h.sub}</div>
                  </div>)}
              </div>

              {/* Education Callout */}
              <div className="bg-dark-card border border-gold/20 rounded-xl p-5" data-id="element-42">
                <div className="text-gold/80 text-xs uppercase tracking-wider font-semibold mb-3" data-id="element-43">
                  Education
                </div>
                <div className="space-y-3" data-id="element-44">
                  <div data-id="element-45">
                    <div className="text-white font-semibold text-sm" data-id="element-46">
                      Wharton School, UPenn
                    </div>
                    <div className="text-slate-400 text-xs mt-0.5" data-id="element-47">
                      CTO Executive Education Program
                    </div>
                  </div>
                  <div className="border-t border-dark-border pt-3" data-id="element-48">
                    <div className="text-white font-semibold text-sm" data-id="element-49">
                      B.S. Electrical Engineering
                    </div>
                    <div className="text-slate-400 text-xs mt-0.5" data-id="element-50">
                      University of Michigan
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column — Bio Narrative */}
            <motion.div variants={fadeInUp} className="lg:col-span-3 space-y-6" data-id="element-51">
              <p className="text-slate-200 text-lg leading-relaxed" data-id="element-52">
                I'm a Fortune 500 Chief Technology Officer with over two decades
                of experience leading large-scale technology transformation in
                highly regulated industries — financial services, banking, and
                insurance. My career has been defined by one consistent mission:
                turning complex, legacy-burdened enterprises into resilient,
                cloud-native organizations that can compete at the speed of
                modern business.
              </p>

              <p className="text-slate-300 leading-relaxed" data-id="element-53">
                Most recently at Truist Financial, I led a $1.2B technology
                portfolio and an engineering organization of 1,700+
                professionals across 40+ countries. I oversaw the migration of
                50,000+ virtual machines to hybrid cloud annually, delivered
                $140M in annual cost savings, and built zero-trust security
                frameworks that achieved zero breaches over six consecutive
                years — maintaining 100% regulatory audit pass rates across SOC
                2, PCI DSS, ISO 27001, and FedRAMP.
              </p>

              <p className="text-slate-300 leading-relaxed" data-id="element-54">
                Prior to Truist, I held senior technology leadership roles at
                Wells Fargo, USAA, AIG, General Motors, Hewlett-Packard,
                Verizon, and Dell — each role deepening my expertise in
                enterprise infrastructure, cyber resiliency, and the
                organizational transformation required to modernize at scale
                within highly regulated environments.
              </p>

              <p className="text-slate-300 leading-relaxed" data-id="element-55">
                I hold a Wharton CTO Executive Education credential and an
                engineering degree. Today, I'm actively seeking my next chapter:
                a CTO, Chief Digital Officer, or board-level technology role
                where I can bring this depth of experience to an organization
                ready to modernize, scale, and lead.
              </p>

              {/* Separator + Key Strengths */}
              <div className="pt-4 border-t border-dark-border" data-id="element-56">
                <div className="text-gold/80 text-xs uppercase tracking-wider font-semibold mb-4" data-id="element-57">
                  Core Strengths
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-58">
                  {['Hybrid Cloud Strategy', 'Cyber Resiliency', 'Regulatory Compliance', 'Infrastructure Modernization', 'Executive Team Building', 'M&A Technology Integration', 'Board Communication', 'P&L Ownership'].map(tag => <span key={tag} className="px-3 py-1.5 bg-dark-card border border-dark-border rounded-full text-slate-300 text-xs font-medium" data-id="element-59">
                      {tag}
                    </span>)}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}